import { useEffect, useState, type ReactNode } from 'react'
import { useLingui } from '@lingui/react/macro'
import type { IndicatorValue, Radar as RadarData } from 'shared'
import { cn } from '@/lib/utils'
import { useIndicators } from '@/hooks/use-indicators'
import { AnimateNumber } from '@/components/custom/animate-number'
import { Avatar } from '@/routes/_app/-components/shared/avatar'
import { Radar } from '@/routes/_app/-components/report/sections/radar/radar'
import { FEATURE_FLAGS } from './feature-flags'
import { ProfileWheel, type WheelOption } from './profile-wheel'

// A sample team whose scores fit its roles (competences in report order: Task 0–5, People 6–11, Context 12–17). The
// names are picked so the avatars' hashed colours differ. The team's profile is the members' rounded mean.
const MEMBERS = [
  // Data Analyst: quality, knowledge and planning high; leading and negotiating low.
  { name: 'Laura M.', scores: [9, 10, 11, 9, 8, 4, 3, 5, 3, 4, 5, 3, 6, 6, 7, 9, 6, 5] },
  // Team Lead: leadership, communication, motivation and supervision high; innovation lower.
  { name: 'Javier R.', scores: [7, 6, 6, 8, 7, 10, 11, 10, 8, 9, 10, 9, 9, 8, 5, 8, 7, 6] },
  // Customer Success: communication, initiative, collaboration and adaptation high; planning and supervision low.
  { name: 'Clara R.', scores: [6, 7, 6, 4, 5, 3, 5, 9, 7, 6, 8, 4, 6, 9, 10, 7, 9, 10] },
] as const
const TEAM = Array.from(
  { length: 18 },
  (_, i) => MEMBERS.reduce((sum, m) => sum + (m.scores[i] ?? 0), 0) / MEMBERS.length,
)
// Profile 0 is the team, 1…3 the members.
const PROFILE_SCORES: readonly (readonly number[])[] = [TEAM, ...MEMBERS.map((member) => member.scores)]

/** What the radar shows: one profile in colour, and optionally a base profile behind it to compare against. */
interface Selection {
  shown: number
  base: number | null
}

// The tour: every profile on its own, then every pair (the earlier profile as the base behind the later one).
const TOUR: readonly Selection[] = [
  ...PROFILE_SCORES.map((_, shown) => ({ shown, base: null })),
  ...PROFILE_SCORES.flatMap((_, base) =>
    Array.from({ length: PROFILE_SCORES.length - base - 1 }, (__, offset) => ({ shown: base + offset + 1, base })),
  ),
]
// The simple variant: only the radar moving, between the team and one of its members (Laura M.) — the team, then her,
// then the two compared.
const DEMOS = {
  team: { scores: PROFILE_SCORES, tour: TOUR },
  simple: {
    scores: [TEAM, MEMBERS[0].scores] as readonly (readonly number[])[],
    tour: [
      { shown: 0, base: null },
      { shown: 1, base: null },
      { shown: 1, base: 0 },
    ] as readonly Selection[],
  },
}
// After the visitor's last touch on a wheel, the tour waits this long, then carries on from what they left on screen.
const RESUME_AFTER_MS = 5000
const DWELL_MS = { single: 2400, pair: 4000 }

const LEVELS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const satisfies readonly IndicatorValue[]
const toValue = (n: number): IndicatorValue => LEVELS[Math.max(0, Math.min(12, Math.round(n)))] ?? 0

// The report's ladders (apps/server/src/report/functions/getProfile.ts): a group scores the first band its sum clears.
const VECTOR_LEVELS = [33, 30, 27, 24, 21, 18, 15, 13, 11, 8, 5, 2]
const DIMENSION_LEVELS = [63, 57, 51, 46, 41, 37, 33, 29, 25, 19, 13, 8]
const overLadder = (sum: number, levels: number[]) => {
  const band = levels.findIndex((level) => sum > level)
  return toValue(band === -1 ? 0 : 12 - band)
}

function rings(scores: readonly number[]) {
  const competences = scores.map(toValue)
  const group = (size: number, count: number, levels: number[]) =>
    Array.from({ length: count }, (_, g) =>
      overLadder(
        competences.slice(g * size, g * size + size).reduce<number>((sum, v) => sum + v, 0),
        levels,
      ),
    )
  return { competences, vectors: group(3, 6, VECTOR_LEVELS), dimensions: group(6, 3, DIMENSION_LEVELS) }
}

/**
 * The radar's input: the base profile as `value`, the profile on show as `valueToCompare` (the same one when alone).
 * Only the competences carry a title, since only the outer ring is labelled.
 */
function comparison(base: readonly number[], shown: readonly number[], names: string[]): RadarData['indicators'] {
  const from = rings(base)
  const to = rings(shown)
  const ring = (key: keyof typeof from) =>
    from[key].map((value, index) => ({
      index,
      title: key === 'competences' ? (names[index] ?? '') : '',
      value,
      valueToCompare: to[key][index] ?? value,
    }))
  // The shared type is a fixed-length tuple per ring; the lengths above are those tuples' lengths.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return {
    competences: ring('competences'),
    vectors: ring('vectors'),
    dimensions: ring('dimensions'),
  } as RadarData['indicators']
}

// Illustrative closeness between two profiles (100 = identical), not the server's match formula.
const match = (a: readonly number[], b: readonly number[]) =>
  Math.round(100 - (a.reduce((sum, v, i) => sum + Math.abs(v - (b[i] ?? v)), 0) / a.length / 11) * 100)

/** The shown profile's mark, in the legend and on the Profile row: the three dimension colours. */
function DimensionDots(props: { className?: string }) {
  return (
    <span className="flex -space-x-1">
      <span className={cn('size-3 rounded-full bg-(--color-radar-blue)', props.className)} />
      <span className={cn('size-3 rounded-full bg-(--color-radar-green)', props.className)} />
      <span className={cn('size-3 rounded-full bg-(--color-radar-yellow)', props.className)} />
    </span>
  )
}

/** The base profile's mark, in the legend and on the Compare row. */
function BaseDot(props: { className?: string }) {
  return <span className={cn('size-3 rounded-full bg-(--color-radar-compare)', props.className)} />
}

// Either way round: the tour lists each pair once, the picker can turn it in either order.
function samePair(a: Selection, b: Selection) {
  return (a.shown === b.shown && a.base === b.base) || (a.shown === b.base && a.base === b.shown)
}

/**
 * The hero's proof. On its own it tours every profile of a sample team, then each member against the team. The legend
 * is also the picker: two wheels, the profile in colour on the left (always one) and the one to compare it with on the
 * right (starting empty). Turning either one takes the radar over; once the visitor lets go for a few seconds, the tour
 * carries on from what they left on screen. The `simple` variant only shows the radar moving — the team, one member, the
 * two compared — with no fit, legend, picker or competence names.
 */
export function RadarCompare(props: { variant?: 'team' | 'simple' }) {
  const variant = props.variant ?? 'team'
  const { scores: demoScores, tour } = DEMOS[variant]
  const { t } = useLingui()
  const { indicators } = useIndicators()
  // The simple variant keeps the radar bare: no competence names around it.
  const names = variant === 'simple' ? [] : indicators.competences.competences.map((competence) => competence.name)
  const team = t`Product team`
  const profiles: { name: string; role: string | undefined; kind: 'team' | 'member' }[] =
    variant === 'simple'
      ? [
          { name: team, role: undefined, kind: 'team' },
          { name: MEMBERS[0].name, role: t`Data Analyst`, kind: 'member' },
        ]
      : [
          { name: team, role: undefined, kind: 'team' },
          ...MEMBERS.map((member, index) => ({
            name: member.name,
            role: [t`Data Analyst`, t`Team Lead`, t`Customer Success`][index],
            kind: 'member' as const,
          })),
        ]

  const [step, setStep] = useState(0)
  const [picked, setPicked] = useState<Selection | null>(null)
  const selection = picked ?? tour[step % tour.length] ?? { shown: 0, base: null }
  const { shown, base } = selection
  const comparing = base !== null

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    if (picked) {
      // Every touch sets a new `picked`, so this timer restarts until the visitor has been idle for the whole pause.
      const id = window.setTimeout(() => {
        const at = tour.findIndex((entry) => samePair(entry, picked))
        setStep(at === -1 ? tour.findIndex((entry) => entry.shown === picked.shown && entry.base === null) : at)
        setPicked(null)
      }, RESUME_AFTER_MS)
      return () => window.clearTimeout(id)
    }
    const id = window.setTimeout(() => setStep((n) => n + 1), comparing ? DWELL_MS.pair : DWELL_MS.single)
    return () => window.clearTimeout(id)
  }, [picked, step, comparing, tour])

  const shownScores = demoScores[shown] ?? TEAM
  const baseScores = base === null ? shownScores : (demoScores[base] ?? TEAM)

  const avatar = (profile: number, size: 'sm' | 'md') => {
    const entry = profiles[profile]
    return entry?.kind === 'team' ? (
      <Avatar type="team" name={entry.name} size={size} className="rounded-md" />
    ) : (
      <Avatar
        type="member"
        name={entry?.name}
        size={size}
        forceInitials
        className={cn('rounded-full', size === 'md' ? 'text-xs' : 'text-[10px]')}
      />
    )
  }
  const entry = (profile: number, mark: ReactNode) => (
    <span className="flex min-w-0 items-center gap-2.5">
      {/* On a phone each wheel is half the width, so the names get the room the marks would take. */}
      <span className="hidden shrink-0 sm:flex">{mark}</span>
      {avatar(profile, 'md')}
      <span className="grid min-w-0 leading-tight">
        <span className="truncate text-base font-medium">{profiles[profile]?.name}</span>
        {profiles[profile]?.role && (
          <span className="text-muted-foreground truncate text-sm">{profiles[profile]?.role}</span>
        )}
      </span>
    </span>
  )
  const shownOptions: WheelOption[] = profiles.map((profile, index) => ({
    id: `radar-shown-${index}`,
    label: profile.name,
    content: entry(index, <DimensionDots className="size-4" />),
  }))
  // The Compare wheel starts on an empty slot; the profile already shown can't be picked against itself.
  const baseOptions: WheelOption[] = [
    // Nothing to compare with: an empty row, named for screen readers only.
    { id: 'radar-base-none', label: t`Compare`, content: <span className="h-8" />, blank: true },
    ...profiles.map((profile, index) => ({
      id: `radar-base-${index}`,
      label: profile.name,
      content: entry(index, <BaseDot className="size-4" />),
      disabled: index === shown,
    })),
  ]

  return (
    <div className="flex flex-col items-center">
      <div className="-mx-5 w-[calc(100%+2.5rem)] sm:-mx-12 sm:w-[calc(100%+6rem)] lg:-mr-16 lg:-ml-12 lg:w-[calc(100%+7rem)]">
        <Radar
          variant="still"
          selected={comparing ? 'compare' : 'first'}
          indicators={comparison(baseScores, shownScores, names)}
        />
      </div>

      {/* Kept in the layout while hidden, so the legend never jumps when the fit comes and goes. The simple variant has
          no fit: it only shows the radar moving. */}
      {variant === 'team' && (
        <span
          aria-hidden={!comparing}
          className={cn(
            'text-foreground mt-4 mb-4 flex items-baseline gap-2 text-4xl font-semibold tracking-tight tabular-nums transition-opacity duration-300',
            !comparing && 'opacity-0',
          )}
        >
          <span>
            <AnimateNumber value={match(baseScores, shownScores)} />%
          </span>
          <small className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">{t`fit`}</small>
        </span>
      )}

      {/* The legend is the picker: the profile in colour on the left, what it is compared with on the right. The simple
          variant has no legend. */}
      {variant === 'team' && (
        <div className="grid w-full max-w-lg grid-cols-2 gap-x-4">
          <ProfileWheel
            interactive={FEATURE_FLAGS.radarPicker}
            label={t`Profile`}
            options={shownOptions}
            selected={shown}
            onSelect={(profile) => setPicked({ shown: profile, base: base === profile ? null : base })}
          />
          <ProfileWheel
            interactive={FEATURE_FLAGS.radarPicker}
            label={t`Compare`}
            options={baseOptions}
            selected={base === null ? 0 : base + 1}
            onSelect={(option) => setPicked({ shown, base: option === 0 ? null : option - 1 })}
          />
        </div>
      )}
    </div>
  )
}
